/**
 * P0-0a 运行能力探测
 *
 * 拆成两层，便于单测：
 *  1. `collectRuntimeFacts()` —— 唯一接触 DOM 的采集层，只做取值不做判断
 *  2. `decideCapability(facts)` —— 纯函数，输入事实输出档位与可用性判定，可离线测试
 *
 * 结论口径（对齐计划书 §11 已决 Q1）：
 *  - 3D 导演台走浏览器本地 WebGL（Three.js r185 起仅支持 WebGL2，无 WebGL1 回退）
 *  - 不支持 WebGL2 或落到软件渲染时，**只禁用导演台模块**，画布/生成/剪辑等其余功能照常可用
 */

export type QualityTier = 'low' | 'mid' | 'high';

/** 采集层产出的原始事实，不含任何判断 */
export interface RuntimeFacts {
  /** 能否创建 WebGL2 上下文 */
  webgl2: boolean;
  /** WEBGL_debug_renderer_info 报出的渲染器名；取不到为 null */
  gpuRenderer: string | null;
  /** 是否被判定为软件渲染器 */
  softwareRenderer: boolean;
  /** devicePixelRatio，取不到为 1 */
  devicePixelRatio: number;
  /** navigator.deviceMemory（GiB），规范只给离散档位，取不到为 null */
  deviceMemoryGiB: number | null;
  /** navigator.hardwareConcurrency，取不到为 null */
  hardwareConcurrency: number | null;
  /** 是否为受支持的浏览器内核（Trident 等旧内核为 false） */
  modernEngine: boolean;
  /** 采集失败时的原因描述，供 UI 直接展示 */
  notices: string[];
}

export interface CapabilityReport extends RuntimeFacts {
  tier: QualityTier;
  /** 能否进入 3D 导演台 */
  directorAvailable: boolean;
  /** 不能进导演台时的可读原因（面向用户，不堆术语） */
  blockingReasons: string[];
}

/**
 * 已知的软件/模拟渲染器特征串。
 * 命中即认为没有真实 GPU 加速：此时 WebGL2 虽然可用，但帧率不可接受，
 * 且浏览器往往不会报错，只会极慢 —— 必须主动提示，不能让专业用户对着幻灯片猜原因。
 */
const SOFTWARE_RENDERER_MARKERS = [
  'swiftshader',
  'llvmpipe',
  'softpipe',
  'basic render',
  'mesa offscreen',
  'microsoft basic',
];

export function isSoftwareRenderer(renderer: string | null): boolean {
  if (renderer === null) {
    return false;
  }
  const needle = renderer.toLowerCase();
  return SOFTWARE_RENDERER_MARKERS.some((marker) => needle.includes(marker));
}

/**
 * 判定是否具备支持 WebGL 的浏览器内核。
 * Trident（IE / 360 兼容模式）完全没有 WebGL；
 * 老 Chromium 内核有 WebGL1 但可能缺 WebGL2，交给 webgl2 事实兜住，这里只识别明确不可用的内核。
 */
export function detectEngine(ua: string): { modernEngine: boolean; label: string } {
  const lower = ua.toLowerCase();
  if (lower.includes('trident') || lower.includes('msie')) {
    return { modernEngine: false, label: 'IE 内核（Trident）' };
  }
  if (lower.includes('edg/') || lower.includes('edga/')) {
    return { modernEngine: true, label: 'Microsoft Edge' };
  }
  if (lower.includes('qihoo')) {
    return { modernEngine: true, label: '360 浏览器（极速/Chromium 内核）' };
  }
  if (lower.includes('chrome') || lower.includes('chromium')) {
    return { modernEngine: true, label: 'Chromium 内核' };
  }
  if (lower.includes('firefox')) {
    return { modernEngine: true, label: 'Firefox' };
  }
  if (lower.includes('safari')) {
    return { modernEngine: true, label: 'Safari' };
  }
  return { modernEngine: true, label: '未知内核' };
}

/** 纯函数：由事实推出档位与可用性。改判定只动这里，测试随之生效。 */
export function decideCapability(facts: RuntimeFacts): CapabilityReport {
  const blockingReasons: string[] = [];

  if (!facts.modernEngine) {
    blockingReasons.push(
      '当前浏览器是不支持 3D 的旧内核（很可能是 360/QQ 浏览器的"兼容模式"）。请在浏览器地址栏右侧或菜单里切换到「极速模式」后刷新。',
    );
  } else if (!facts.webgl2) {
    blockingReasons.push(
      '当前浏览器无法创建 WebGL2 图形环境。请更新浏览器到最新版，并在设置中开启「使用硬件加速（如可用）」后重启浏览器。',
    );
  } else if (facts.softwareRenderer) {
    blockingReasons.push(
      `检测到图形渲染由软件模拟（${facts.gpuRenderer ?? '未知渲染器'}），3D 预演会严重卡顿。请在浏览器设置中开启「使用硬件加速（如可用）」后重启浏览器；若显卡过旧，建议改用有独立/较新集成显卡的机器。`,
    );
  }

  const directorAvailable = blockingReasons.length === 0;

  // 档位只在"能用"的前提下细分；不能用时给 low，让降级路径唯一
  let tier: QualityTier = 'low';
  if (directorAvailable) {
    const mem = facts.deviceMemoryGiB;
    const cores = facts.hardwareConcurrency;
    const heavyGpu = /(rtx|gtx|radeon rx|arc a[3-9]|quadro|geforce|rpro)/i.test(facts.gpuRenderer ?? '');

    const strong =
      (mem === null || mem >= 8) &&
      (cores === null || cores >= 8) &&
      heavyGpu &&
      facts.devicePixelRatio <= 2;

    const weak =
      (mem !== null && mem <= 4) ||
      (cores !== null && cores <= 4) ||
      (facts.gpuRenderer !== null && /hd graphics|uhd|intel\(r\)\s*hd/i.test(facts.gpuRenderer));

    if (strong) {
      tier = 'high';
    } else if (!weak) {
      tier = 'mid';
    }
    // weak 保持 low；显存不足以确认时宁可低估，也不能让用户第一次就撞上掉帧
  }

  return {
    ...facts,
    tier,
    directorAvailable,
    blockingReasons,
  };
}

/**
 * `navigator.deviceMemory` 是 Chromium 系私有 API，不在 TS 标准 DOM 类型里。
 * 用显式接口扩展读取，避免 any 与漏判：非 Chromium 浏览器上恒为 null，
 * 档位判定会退回「只看核数与 GPU 名」的路径，这是预期行为。
 */
interface NavigatorWithDeviceMemory extends Navigator {
  readonly deviceMemory?: number;
}

function readDeviceMemoryGiB(): number | null {
  const nav = navigator as NavigatorWithDeviceMemory;
  return typeof nav.deviceMemory === 'number' ? nav.deviceMemory : null;
}

/** 采集层。只在浏览器运行时调用；服务端渲染/测试环境请改用 decideCapability 直接喂事实。 */
export function collectRuntimeFacts(): RuntimeFacts {
  const notices: string[] = [];
  let webgl2 = false;
  let renderer: string | null = null;

  try {
    const canvas = document.createElement('canvas');
    const gl = canvas.getContext('webgl2', {
      failIfMajorPerformanceCaveat: true,
      powerPreference: 'high-performance',
    });
    if (gl !== null) {
      webgl2 = true;
      const dbg = gl.getExtension('WEBGL_debug_renderer_info');
      if (dbg !== null) {
        renderer = String(gl.getParameter(dbg.UNMASKED_RENDERER_WEBGL) ?? '') || null;
      }
      // 立刻释放上下文：浏览器对活动 WebGL 上下文数量有限制，别占着导演台的额度
      gl.getExtension('WEBGL_lose_context')?.loseContext();
    }
  } catch (error) {
    notices.push(`WebGL2 探测异常：${error instanceof Error ? error.message : String(error)}`);
  }

  const engine = detectEngine(navigator.userAgent);

  return {
    webgl2,
    gpuRenderer: renderer,
    softwareRenderer: isSoftwareRenderer(renderer),
    devicePixelRatio: window.devicePixelRatio || 1,
    deviceMemoryGiB: readDeviceMemoryGiB(),
    hardwareConcurrency:
      typeof navigator.hardwareConcurrency === 'number' ? navigator.hardwareConcurrency : null,
    modernEngine: engine.modernEngine,
    notices,
  };
}

export function probeCapability(): CapabilityReport {
  return decideCapability(collectRuntimeFacts());
}

export function engineLabel(): string {
  return detectEngine(navigator.userAgent).label;
}
